import React from 'react';

import CustomHead from '../components/CustomHead';
import Footer from '../components/FooterNew';
import Header from '../components/HeaderNew';
import { SectionContainer } from '../components/landing/sectionComponents';
import StandardFlex from '../components/landing/StandardFlex';
import Video from '../components/Video';
import useGa from '../hooks/useGa';

import CtaForm from '@components/common/CtaForm';
import Heading from '@components/Heading';
import HeroBottom from '@components/HeroBottom';

const dbtHeadingPath = `${process.env.NEXT_PUBLIC_ASSET_HOST}/static/features/dbt/dbtpopsql.png`;

const sql = {
  noMacro: (
    <>
      <div>select</div>
      <div>{`   `}created_at at time zone ‘utc’ at time zone ‘pdt’</div>
      <div>from users</div>
    </>
  ),
  macro: (
    <>
      <div>
        select{' '}
        <span
          className="font-medium"
          style={{ color: `#00A3FF` }}
        >{`{{ to_pt(‘created_at’) }}`}</span>{' '}
        from users
      </div>
    </>
  ),
  noModel: (
    <>
      <div>with activated_users as (</div>
      <div>{`   `}select</div>
      <div>{`   `}users.id as user_id,</div>
      <div>{`   `}count(orders.id) as num_orders</div>
      <div>{`   `}from users</div>
      <div>{`   `}left join orders on orders.user_id = users.id</div>
      <div>{`   `}group by 1</div>
      <div>
        {`   `}having count(orders.id) {'>'} 0
      </div>
      <div>)</div>
      <div></div>
      <div>select *</div>
      <div>from events e</div>
      <div>inner join activated_users au</div>
      <div>on au.user_id = e.user_id</div>
    </>
  ),
  model: (
    <>
      <div>select * </div>
      <div>from events e</div>
      <div>
        inner join{' '}
        <span
          className="font-medium"
          style={{ color: `#00A3FF` }}
        >{`{{ ref('activated_users') }}`}</span>{' '}
        au on au.user_id = e.user_id
      </div>
    </>
  ),
};

const CodeSnippet = ({ code }) => {
  return (
    <div
      style={{ border: `1px solid rgba(64, 75, 85, 1)` }}
      className="rounded-lg py-3 my-3 text-sm mx-auto overflow-x-auto overflow-y-hidden w-screen lg:w-full"
    >
      <pre className="text-white count-lines">
        <code>{code}</code>
      </pre>
    </div>
  );
};

const BeforeAfter = ({ before, after }) => {
  return (
    <>
      <div>
        <div className="my-4 inline-block border border-cobalt-300 text-white rounded-full px-4 py-2 font-inter font-medium">
          Before
        </div>
        <div>
          <CodeSnippet code={before} />
        </div>
      </div>

      <div>
        <div className="my-4 text-black bg-white rounded-full px-4 py-2 inline-block font-inter font-medium">
          <div className="flex items-center space-x-3">
            <span>After</span>
          </div>
        </div>
        <div>
          <CodeSnippet code={after} />
        </div>
      </div>
    </>
  );
};

const Dbt = () => {
  return (
    <>
      <CustomHead
        meta={{
          title:
            'The first SQL editor with built-in dbt Core™ support | PopSQL',
          description: `Create dbt macros and models, all within your SQL editor`,
          image: `${process.env.NEXT_PUBLIC_ASSET_HOST}/static/features/dbt/og.jpg`,
        }}
      />
      <main>
        <Header />

        <div className="relative -mb-16">
          <SectionContainer>
            <img
              style={{ width: `200px` }}
              className="mx-auto"
              src={dbtHeadingPath}
            />
            <Heading Tag="h1" size="xl" className="max-w-5xl mt-8 text-center">
              The first SQL editor with built-in dbt Core
              <span
                style={{ position: `relative`, fontSize: 20, top: `-1.2em` }}
              >
                ™
              </span>{' '}
              support
            </Heading>
            <p className="my-2 sm:my-6 text-center max-w-3xl text-gray-500 mx-auto dark:text-cobalt-600 leading-normal text-md lg:text-xl font-inter fade fade-2">
              Create dbt macros and models, all within your SQL editor
            </p>
            <CtaForm
              onClickFn={useGa('Signup', 'Hero Features')}
              variant="dark"
            />
            <div className="mx-auto mt-16">
              <div className="rounded-lg overflow-hidden">
                <Video
                  name="dbt demo"
                  image={`${process.env.NEXT_PUBLIC_ASSET_HOST}/static/features/dbt/video_thumbnail.jpg`}
                  wistiaId="i729xh5jd4"
                />
              </div>
            </div>
          </SectionContainer>
        </div>

        <SectionContainer>
          <StandardFlex
            heading={
              <>
                <div className="text-3xl">
                  Eliminate repetitive SQL using macros
                </div>
              </>
            }
            subheading="Instead of copying/pasting SQL between queries, create reusable snippets of SQL called macros."
            customContent={
              <div className="lg:pl-24 mb-24">
                <BeforeAfter
                  before={sql.noMacro}
                  after={sql.macro}
                  highlight={'2'}
                />
              </div>
            }
          />

          <StandardFlex
            isReverse
            heading={
              <>
                <div className="text-3xl">
                  Simplify BI queries with data models
                </div>
              </>
            }
            subheading="Use models to break down long, complicated queries."
            link={{
              text: 'Read the docs',
              href: '/docs/dbt',
              asPath: '/docs/dbt',
            }}
            customContent={
              <div className="lg:pr-16 mb-24">
                <BeforeAfter
                  before={sql.noModel}
                  after={sql.model}
                  highlight={'3'}
                />
              </div>
            }
          />

          <StandardFlex
            heading={
              <>
                <div className="text-3xl">
                  Create a data catalog backed by dbt docs
                </div>
              </>
            }
            subheading="Write table/column descriptions in dbt, and have those descriptions available in PopSQL autocomplete."
            customContent={
              <>
                <img
                  className="w-full my-16"
                  src={`${process.env.NEXT_PUBLIC_ASSET_HOST}/static/features/dbt/docs.png`}
                />
              </>
            }
          />

          <StandardFlex
            isReverse
            heading={
              <>
                <div className="text-3xl">Streamline your dbt workflow</div>
              </>
            }
            subheading="Do your entire dbt workflow within PopSQL rather than switching between your code editor, terminal, and SQL editor."
            customContent={
              <>
                <img
                  className="w-full my-16"
                  src={`${process.env.NEXT_PUBLIC_ASSET_HOST}/static/features/dbt/workflow.png`}
                />
              </>
            }
          />

          <HeroBottom
            icon={`${process.env.NEXT_PUBLIC_ASSET_HOST}/static/homepage/popsql_app_icon.png`}
            title="The most powerful workspace for data teams"
            description=""
            spacing="mt-20 md:mt-52 mb-20 md:mb-40 px-4 gap-4"
          />

          <div className="text-center mx-auto opacity-50">
            <p className="text-center max-w-3xl text-gray-500 mx-auto dark:text-cobalt-600 leading-normal text-xs font-inter fade fade-2">
              dbt, dbt Core, and the dbt logo are trademarks of dbt Labs, Inc.
            </p>
          </div>
        </SectionContainer>
      </main>

      <Footer />
    </>
  );
};

export default Dbt;
